import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, MenuItem, Select, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useApiClient, useToastService } from '@@common';

import { FlowClient } from '../../../api/api';
import { ServiceConnectionRefModel } from '../Models/ServiceConnectionModel';
import ServiceConnectionPicker from './Inputs/ServiceConnectionPicker';

type Props = { flowId: string; open: boolean; onClose: () => void };

export default function FlowConnectionBindingsDialog({ flowId, open, onClose }: Props) {
	const client = useApiClient(FlowClient);
	const queryClient = useQueryClient();
	const toast = useToastService();
	const [selectedSlot, setSelectedSlot] = useState<string>();
	const { data, isLoading } = useQuery({
		queryKey: ['flow', 'connectionBindings', flowId],
		queryFn: () => client.getConnectionBindings(flowId),
		enabled: open,
	});
	const setBinding = useMutation({
		mutationFn: ({ sourceId, targetId }: { sourceId: string; targetId: string }) =>
			client.setConnectionBinding(flowId, sourceId, { tenantConnectionId: targetId }),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['flow', 'connectionBindings', flowId] });
			toast.success('Service connection assigned');
		},
	});
	const removeBinding = useMutation({
		mutationFn: (sourceId: string) => client.removeConnectionBinding(flowId, sourceId),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['flow', 'connectionBindings', flowId] });
			toast.success('Service connection unassigned');
		},
	});
	const setPolicy = useMutation({
		mutationFn: ({ sourceId, policy }: { sourceId: string; policy: string }) => client.setConnectionPolicy(flowId, sourceId, { policy }),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['flow', 'connectionBindings', flowId] });
			toast.success('Connection policy saved');
		},
		onError: (error) => toast.error(error instanceof Error ? error.message : 'Could not save the connection policy'),
	});
	const isSaving = setBinding.isPending || removeBinding.isPending || setPolicy.isPending;
	const canEditPolicies = data?.canEditPolicies === true;
	const visibleSlots = data?.slots.filter((item) => canEditPolicies || item.policy !== 'Fixed') ?? [];
	const slot = visibleSlots.find((item) => item.sourceConnectionId === selectedSlot);
	const value: ServiceConnectionRefModel | undefined = slot?.tenantConnectionId
		? { Id: slot.tenantConnectionId, Name: slot.tenantConnectionName ?? '' }
		: undefined;

	const handleSelected = async (connection: ServiceConnectionRefModel) => {
		if (!slot) return false;
		try {
			await setBinding.mutateAsync({ sourceId: slot.sourceConnectionId, targetId: connection.Id });
			return true;
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Could not save the service connection');
			return false;
		}
	};

	const handleClose = () => {
		if (isSaving) {
			return;
		}

		setSelectedSlot(undefined);
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth
			maxWidth={canEditPolicies ? 'sm' : 'lg'}
			slotProps={{ paper: { sx: { width: canEditPolicies ? undefined : 1000 } } }}
		>
			<DialogTitle>{canEditPolicies ? 'Connection policies' : 'Configure service connections'}</DialogTitle>
			<DialogContent dividers sx={{ maxHeight: '75vh' }}>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					{canEditPolicies
						? 'Choose whether tenants may use the Randol connection or must provide their own.'
						: 'Choose connections owned by your tenant. Each connection manages its own environment settings.'}
				</Typography>
				<Box sx={{ display: 'grid', gridTemplateColumns: slot ? { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' } : '1fr', gap: { xs: 2, md: 3 } }}>
					<Stack spacing={2} sx={{ minWidth: 0 }}>
						{isLoading && <Typography>Loading connections…</Typography>}
						{visibleSlots.map((item) => {
							const isSelectable = !canEditPolicies && item.policy !== 'Fixed';
							const isSelected = selectedSlot === item.sourceConnectionId;
							return (
							<Box
								key={item.sourceConnectionId}
								role={isSelectable ? 'button' : undefined}
								tabIndex={isSelectable ? 0 : undefined}
								aria-label={isSelectable ? `Choose connection for ${item.name}` : undefined}
								aria-pressed={isSelectable ? isSelected : undefined}
								onClick={isSelectable && !isSaving ? () => setSelectedSlot(item.sourceConnectionId) : undefined}
								onKeyDown={isSelectable ? (event) => {
									if (!isSaving && event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
										event.preventDefault();
										setSelectedSlot(item.sourceConnectionId);
									}
								} : undefined}
								sx={{
									position: 'relative',
									display: 'grid',
									gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
									alignItems: 'center',
									columnGap: 1,
									rowGap: 1,
									minHeight: 88,
									p: 1,
									borderRadius: 1,
									border: 2,
									borderColor: isSelected ? 'primary.main' : 'divider',
									bgcolor: isSelected ? (theme) => alpha(theme.palette.primary.main, 0.12) : undefined,
									...(isSelectable && {
										cursor: 'pointer',
										'&:hover': { borderColor: 'primary.main' },
										'&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
									}),
									...(isSelected && {
										'&::after': {
											content: '""',
											position: 'absolute',
											left: '100%',
											top: '50%',
											width: (theme) => theme.spacing(3),
											borderTop: 2,
											borderColor: 'primary.main',
											display: { xs: 'none', md: 'block' },
										},
									}),
								}}
							>
								<Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
									<Typography fontWeight={600}>{item.name}</Typography>
									<Typography variant="caption" color="text.secondary" display="block">
										{item.type}
									</Typography>
									{!canEditPolicies && (
										item.tenantConnectionId ? (
											<Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.5, color: 'primary.main' }}>
												<ArrowBackIcon sx={{ fontSize: 18, flexShrink: 0, alignSelf: 'center' }} aria-hidden="true" />
												<Typography variant="body2" fontWeight={600} sx={{ lineHeight: '20px' }}>
													{item.tenantConnectionName || 'Tenant connection'}
												</Typography>
												{isSelectable && (
													<Button size="small" color="error" loading={removeBinding.isPending && removeBinding.variables === item.sourceConnectionId} sx={{ minWidth: 0, height: 20, px: 0.5, py: 0, fontSize: '0.875rem', lineHeight: '20px' }} onClick={(event) => {
														event.stopPropagation();
														if (!isSaving) {
															removeBinding.mutate(item.sourceConnectionId);
														}
													}}>
														Unassign
													</Button>
												)}
											</Box>
										) : (
											<Typography variant="body2">
												{item.policy === 'Required' ? 'Tenant connection required' : 'Using Randol connection'}
											</Typography>
										)
									)}
								</Box>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifySelf: 'end',
										gap: 0.5,
										maxWidth: '100%',
									}}
								>
									{canEditPolicies ? (
										<FormControl size="small">
											<Select
												aria-label={`Policy for ${item.name}`}
												value={item.policy}
												disabled={isSaving}
												onChange={(event) => setPolicy.mutate({ sourceId: item.sourceConnectionId, policy: event.target.value })}
											>
												<MenuItem value="Fixed">Fixed Randol connection</MenuItem>
												<MenuItem value="Optional">Optional tenant override</MenuItem>
												<MenuItem value="Required">Required tenant override</MenuItem>
											</Select>
										</FormControl>
									) : (
										<>
											<Stack spacing={0.5} alignItems="flex-end">
												<Chip
													size="small"
													variant="outlined"
													label={item.policy === 'Fixed' ? 'Randol managed' : item.policy === 'Optional' ? 'Override optional' : 'Override required'}
												/>
												<Chip size="small" color={item.isValid ? 'success' : 'warning'} label={item.isValid ? 'Ready' : 'Setup required'} />
											</Stack>
										</>
									)}
								</Box>
							</Box>
							);
						})}
						{data && visibleSlots.length === 0 && (
							<Typography>
								{data.slots.length === 0
									? 'This flow does not require a service connection.'
									: 'This flow has no service connections available to override.'}
							</Typography>
						)}
					</Stack>
					{slot && (
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								minWidth: 0,
								border: 2,
								borderColor: 'primary.main',
								borderRadius: 1,
								bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
								p: 2,
							}}
						>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
								Choose a {slot.type} connection for this slot.
							</Typography>
							<ServiceConnectionPicker
								key={slot.sourceConnectionId}
								serviceConnectionType={slot.type}
								value={value}
								confirmLabel="Assign"
								onSave={handleSelected}
								onCancel={() => {
									if (!isSaving) {
										setSelectedSlot(undefined);
									}
								}}
							/>
						</Box>
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} loading={isSaving}>Close</Button>
			</DialogActions>
		</Dialog>
	);
}
